import { useState } from 'react'

import { Check, MoreHorizontal } from 'lucide-react'

import { Button } from '@/components/common/Button/Button'
import { cn } from '@/utils/cn'

import type { DensityToggleProps, TableDensity } from '../DataTable.types'

const DENSITY_OPTIONS: Array<{
  value: TableDensity
  label: string
  description: string
}> = [
  {
    value: 'compact',
    label: 'Compacto',
    description: 'Más filas en menos espacio'
  },
  {
    value: 'normal',
    label: 'Normal',
    description: 'Espaciado estándar'
  },
  {
    value: 'comfortable',
    label: 'Cómodo',
    description: 'Más espacio entre filas'
  }
]

export const DensityToggle: React.FC<DensityToggleProps> = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className={cn('relative', className)}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Cambiar densidad de la tabla"
      >
        <MoreHorizontal className="h-4 w-4 mr-2" />
        Densidad
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-2 w-48 bg-background border rounded-md shadow-lg z-50">
            <div className="p-2">
              <h3 className="font-medium text-sm mb-2 px-2">Densidad de tabla</h3>

              <div className="space-y-1">
                {DENSITY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    className={cn(
                      'w-full flex items-start gap-3 px-2 py-2 text-left hover:bg-muted rounded',
                      'transition-colors'
                    )}
                    onClick={() => {
                      onChange(option.value)
                      setIsOpen(false)
                    }}
                  >
                    <div className="flex items-center justify-center w-4 h-4 mt-0.5">
                      {value === option.value ? (
                        <Check className="h-3 w-3 text-primary" />
                      ) : (
                        <div className="w-3 h-3" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{option.label}</div>
                      <div className="text-xs text-muted-foreground">{option.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Compact version for mobile
export const CompactDensityToggle: React.FC<DensityToggleProps> = ({
  value,
  onChange,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const currentOption = DENSITY_OPTIONS.find((option) => option.value === value)

  return (
    <div className={cn('relative', className)}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2"
        aria-label={`Densidad: ${currentOption?.label}`}
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-40 bg-background border rounded-md shadow-lg z-50">
            <div className="p-1">
              {DENSITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted rounded text-sm',
                    value === option.value && 'bg-muted'
                  )}
                  onClick={() => {
                    onChange(option.value)
                    setIsOpen(false)
                  }}
                >
                  {value === option.value && <Check className="h-3 w-3 text-primary" />}
                  <span className={cn(value !== option.value && 'ml-5')}>{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// Inline density controls (for always-visible controls)
export const InlineDensityToggle: React.FC<DensityToggleProps> = ({
  value,
  onChange,
  className
}) => {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {DENSITY_OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => onChange(option.value)}
          className="px-2 py-1 text-xs"
          aria-label={`Densidad ${option.label}`}
        >
          {option.label.charAt(0)}
        </Button>
      ))}
    </div>
  )
}

// Preview component showing density examples
export const DensityPreview: React.FC<{
  density: TableDensity
  className?: string
}> = ({ density, className }) => {
  const heights = {
    compact: 'h-6',
    normal: 'h-10',
    comfortable: 'h-12'
  }

  return (
    <div className={cn('flex flex-col gap-1 p-2 border rounded', className)}>
      <div className="text-xs font-medium text-muted-foreground mb-1">Vista previa</div>
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          className={cn('bg-muted rounded flex items-center px-2', heights[density])}
        >
          <div className="text-xs text-muted-foreground">Fila {index + 1}</div>
        </div>
      ))}
    </div>
  )
}
